/** @jsxImportSource @emotion/react */
import { EmptyContent, Header } from '@quickbit/backoffice-core-frontend';
import {
  createStyles,
  Input,
  Loader,
  Table,
  Typography,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChainalysisLookupResult,
  ChainalysisTransferRow,
  ChainalysisUserLookupResult,
  useChainalysisLookup,
  useChainalysisUserLookup,
} from 'api/useChainalysisLookupApi';

const CATEGORY_NAMES: Record<number, string> = {
  1: 'CHILD_ABUSE_MATERIAL',
  2: 'DARKNET_MARKET',
  3: 'SANCTIONED_ENTITY',
  6: 'STOLEN_FUNDS',
  12: 'RANSOMWARE',
  18: 'SCAM',
  23: 'TERRORIST_FINANCING',
  25: 'SANCTIONED_JURISDICTION',
  28: 'FRAUD_SHOP',
  29: 'ILLICIT_ACTOR_ORGANIZATION',
  34: 'SPECIAL_MEASURES',
  35: 'MALWARE',
  36: 'ONLINE_PHARMACY',
  39: 'SEIZED_FUNDS',
  42: 'STOLEN_BITCOINS',
  43: 'STOLEN_ETHER',
  48: 'EU_20TH_SANCTIONS_PACKAGE',
  999: 'CUSTOM_ADDRESS',
};

type Mode = 'user' | 'transaction';

const parseIds = (raw: string): string[] => {
  const parts = raw.split(/[\s,;]+/).map((s) => s.trim());
  const seen = new Set<string>();
  const ids: string[] = [];
  parts.forEach((p) => {
    if (p && !seen.has(p)) {
      seen.add(p);
      ids.push(p);
    }
  });
  return ids;
};

const formatCluster = (cluster: {
  name: string | null;
  categoryId: number | null;
}): string => {
  const parts: string[] = [];
  if (cluster.name) parts.push(cluster.name);
  if (cluster.categoryId !== null) {
    parts.push(
      CATEGORY_NAMES[cluster.categoryId] ?? `Category ${cluster.categoryId}`
    );
  }
  return parts.length ? parts.join(' · ') : '—';
};

const AlertsCell = ({
  alerts,
}: {
  alerts: ChainalysisTransferRow['alerts'];
}) => {
  if (alerts.length === 0) {
    return <Typography variant="bodySmallest">—</Typography>;
  }
  return (
    <div>
      {alerts.map((alert, i) => (
        <Typography key={i} variant="bodySmallest">
          {alert.level}
          {alert.category ? ` · ${alert.category}` : ''} · {alert.alertStatus}
          {alert.handledByUs ? ' · handled by us' : ' · not handled'}
        </Typography>
      ))}
    </div>
  );
};

const USER_MODE_HEADERS = [
  { label: 'User ID', cellKey: 'userId' },
  { label: 'Direction', cellKey: 'direction' },
  { label: 'Asset', cellKey: 'asset' },
  { label: 'Amount', cellKey: 'amount' },
  { label: 'Amount (USD)', cellKey: 'amountUsd' },
  { label: 'Cluster / Category', cellKey: 'cluster' },
  { label: 'Alerts', cellKey: 'alerts' },
  { label: 'In Quickbit', cellKey: 'qbTransaction' },
  { label: 'Notes', cellKey: 'notes' },
];

const TX_MODE_HEADERS = [
  { label: 'Transaction ID', cellKey: 'transactionId' },
  { label: 'Type', cellKey: 'transactionType' },
  { label: 'Asset', cellKey: 'asset' },
  { label: 'Rating', cellKey: 'rating' },
  { label: 'Cluster / Category', cellKey: 'cluster' },
  { label: 'Notes', cellKey: 'notes' },
];

type TableRow = { id: string; value: Record<string, React.ReactNode> };

const buildUserRows = (results: ChainalysisUserLookupResult[]): TableRow[] =>
  results.flatMap((result): TableRow[] => {
    if (result.transfers.length === 0) {
      return [
        {
          id: result.userId,
          value: {
            userId: (
              <Typography variant="bodySmallest">{result.userId}</Typography>
            ),
            direction: '—',
            asset: '—',
            amount: '—',
            amountUsd: '—',
            cluster: '—',
            alerts: '—',
            qbTransaction: '—',
            notes: result.notes ?? '',
          },
        },
      ];
    }
    return result.transfers.map((transfer, i) => ({
      id: `${result.userId}-${transfer.direction}-${transfer.transferReference}-${i}`,
      value: {
        userId: <Typography variant="bodySmallest">{result.userId}</Typography>,
        direction: transfer.direction,
        asset: transfer.asset,
        amount: transfer.amount,
        amountUsd: transfer.amountUsd,
        cluster: transfer.cluster ? formatCluster(transfer.cluster) : '—',
        alerts: <AlertsCell alerts={transfer.alerts} />,
        qbTransaction: transfer.qbTransactionId ? (
          <Link to={`/transactions/${transfer.qbTransactionId}`}>
            <Typography variant="bodySmallest">Yes — view</Typography>
          </Link>
        ) : (
          'No'
        ),
        notes: '',
      },
    }));
  });

const buildTransactionRows = (results: ChainalysisLookupResult[]): TableRow[] =>
  results.map((result) => ({
    id: result.transactionId,
    value: {
      transactionId: (
        <Typography variant="bodySmallest">{result.transactionId}</Typography>
      ),
      transactionType: result.transactionType ?? '—',
      asset: result.asset ?? '—',
      rating: result.rating ?? '—',
      cluster: result.cluster ? formatCluster(result.cluster) : '—',
      notes: result.notes ?? '',
    },
  }));

export const ChainalysisLookup = () => {
  const styles = useStyles();
  const [mode, setMode] = useState<Mode>('user');
  const [rawInput, setRawInput] = useState('');
  const userLookup = useChainalysisUserLookup();
  const transactionLookup = useChainalysisLookup();

  const active = mode === 'user' ? userLookup : transactionLookup;
  const parsedCount = parseIds(rawInput).length;

  const submit = () => {
    const ids = parseIds(rawInput);
    if (ids.length === 0) return;
    if (mode === 'user') {
      userLookup.mutate({ userIds: ids });
    } else {
      transactionLookup.mutate({ transactionIds: ids });
    }
  };

  const headers = mode === 'user' ? USER_MODE_HEADERS : TX_MODE_HEADERS;
  const rows =
    mode === 'user'
      ? userLookup.data
        ? buildUserRows(userLookup.data.results)
        : []
      : transactionLookup.data
      ? buildTransactionRows(transactionLookup.data.results)
      : [];

  return (
    <>
      <Header>
        <Typography variant="subtitle">Chainalysis Lookup</Typography>
      </Header>

      <div css={styles.form}>
        <div css={styles.modeToggle}>
          <UtilityButton
            text="By user"
            variant={mode === 'user' ? 'primary' : 'secondary'}
            onClick={() => setMode('user')}
          />
          <UtilityButton
            text="By transaction"
            variant={mode === 'transaction' ? 'primary' : 'secondary'}
            onClick={() => setMode('transaction')}
          />
        </div>
        <Typography variant="bodySmall">
          {mode === 'user'
            ? 'Paste one or more user IDs. For each user you get every ' +
              'transfer registered with Chainalysis, its risk flags and ' +
              'alerts, and whether the transaction exists in Quickbit.'
            : 'Paste one or more Quickbit transaction IDs. Only external ' +
              'crypto sends and receives are screened by Chainalysis.'}
        </Typography>
        <Input
          type="textarea"
          value={rawInput}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setRawInput(e.target.value)
          }
          label={mode === 'user' ? 'User IDs' : 'Transaction IDs'}
        />
        <div css={styles.actions}>
          <UtilityButton
            text={
              active.isPending
                ? 'Looking up...'
                : `Look up ${parsedCount || ''}`.trim()
            }
            variant="primary"
            onClick={submit}
            disabled={active.isPending || parsedCount === 0}
          />
        </div>
      </div>

      {active.isPending && (
        <div css={styles.loadingContainer}>
          <Loader />
        </div>
      )}

      {active.isError && (
        <EmptyContent
          heading="Lookup failed"
          body={active.error.errorMessage ?? 'Please try again'}
          action={
            <UtilityButton
              text="Try again"
              variant="primary"
              onClick={submit}
            />
          }
        />
      )}

      {!active.isPending && rows.length > 0 && (
        <div css={styles.container}>
          <Table headers={headers} rows={rows} />
        </div>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    marginTop: spacing.xxs,
    marginBottom: spacing.s,
  },
  modeToggle: {
    display: 'flex',
    gap: spacing.xxs,
  },
  actions: {
    display: 'flex',
    gap: spacing.xxs,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
    overflowX: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: spacing.l,
  },
}));
